import { SlotDto, WeekPlanDto } from "@growth/dto";
import {
  CONTENT_FORMATS,
  ContentFormat,
  PIECE_STATUS_LABELS,
  PieceStatus,
  Platform,
  PLATFORMS,
} from "@growth/schema";
import { Button, cn, Field, Input, Textarea } from "@growth/ui";
import {
  CalendarPlus,
  ChevronLeft,
  ChevronRight,
  LoaderCircle,
  Plus,
  Sparkles,
  X,
} from "lucide-react";
import { type FormEvent, useMemo, useState } from "react";
import { Link } from "react-router-dom";

import { ErrorText, PageHeader, selectClass } from "../components/page-header";
import { useActive } from "../lib/active";
import { apiErrorMessage } from "../lib/api-error";
import {
  useAddSlot,
  useApprovePlan,
  useCalendar,
  useCancelSlot,
  useMoveSlot,
  useRequestPlan,
} from "../lib/calendar";
import { useContentSettings } from "../lib/content";

/** Colour per platform; the short name always shows too, so colour is never the only cue. */
export const PLATFORM_STYLE: Record<string, string> = {
  linkedin: "bg-[#dbe7fb] text-[#0b3d91]",
  instagram: "bg-[#f6dde8] text-[#8a1c4a]",
  tiktok: "bg-[#1d1d1f] text-white",
  snapchat: "bg-[#fff6a8] text-[#4a4300]",
  facebook: "bg-[#e1e9fb] text-[#1b3f8f]",
  reddit: "bg-[#fde4d8] text-[#9a2f00]",
  threads: "bg-[#d9d4ec] text-[#2e2459]",
  x: "bg-[#e4e4e4] text-[#111111]",
};

const DAY = 86_400_000;
const iso = (time: number) => new Date(time).toISOString().slice(0, 10);
const addDays = (date: string, days: number) => iso(Date.parse(`${date}T00:00:00Z`) + days * DAY);
const mondayOf = (date: string) => {
  const day = new Date(`${date}T00:00:00Z`).getUTCDay();
  return addDays(date, -((day + 6) % 7));
};
const label = (date: string, options: Intl.DateTimeFormatOptions) =>
  new Date(`${date}T00:00:00Z`).toLocaleDateString("en-AU", { timeZone: "UTC", ...options });

export const slotStatus = (slot: SlotDto) =>
  slot.piece
    ? (PIECE_STATUS_LABELS[slot.piece.status as PieceStatus] ?? slot.piece.status)
    : slot.status === "proposed"
      ? "Proposed"
      : "Planned";

const formatLabel = (format: string) =>
  CONTENT_FORMATS[format as ContentFormat]?.replace(
    /^(LinkedIn|Instagram|TikTok|Snapchat|Facebook|Reddit|Threads|X) /,
    "",
  ) ?? format;

/** The month: the director's week plans to approve, every post by day, and what's on each day. */
export const CalendarPage = () => {
  const { brand, atLeast } = useActive();
  const todayLocal = new Date().toLocaleDateString("en-CA");
  const [month, setMonth] = useState(todayLocal.slice(0, 7));
  const first = `${month}-01`;
  const from = mondayOf(first);
  const to = addDays(from, 41);
  const calendar = useCalendar(from, to);
  const [selected, setSelected] = useState(todayLocal);
  const [hidden, setHidden] = useState<Set<string>>(new Set());
  const today = calendar.data?.today ?? todayLocal;

  const shift = (months: number) => {
    const [y, m] = month.split("-").map(Number) as [number, number];
    const next = new Date(Date.UTC(y, m - 1 + months, 1));
    setMonth(next.toISOString().slice(0, 7));
  };

  const byDay = useMemo(() => {
    const map = new Map<string, SlotDto[]>();
    for (const slot of calendar.data?.slots ?? []) {
      if (hidden.has(slot.channel)) continue;
      map.set(slot.date, [...(map.get(slot.date) ?? []), slot]);
    }
    return map;
  }, [calendar.data, hidden]);
  const days = Array.from({ length: 42 }, (_, index) => addDays(from, index));
  const plans = (calendar.data?.plans ?? []).filter((plan) => plan.status !== "approved");

  return (
    <section className="max-w-7xl space-y-5">
      <PageHeader
        eyebrow={brand?.name ?? ""}
        title={label(first, { month: "long", year: "numeric" })}
        actions={
          <>
            <Button variant="secondary" aria-label="Previous month" onClick={() => shift(-1)}>
              <ChevronLeft className="size-4" />
            </Button>
            <Button variant="secondary" aria-label="Next month" onClick={() => shift(1)}>
              <ChevronRight className="size-4" />
            </Button>
          </>
        }
      >
        The director plans each week from the research brief; nothing is made until someone approves
        the week. Each post's channel owner then approves it on Today.
      </PageHeader>

      {atLeast("EDITOR") && <PlanWeek today={today} />}
      {plans.map((plan) => (
        <PlanBanner
          key={plan.id}
          plan={plan}
          count={
            calendar.data?.slots.filter(
              (slot) => slot.planId === plan.id && slot.status === "proposed",
            ).length ?? 0
          }
        />
      ))}

      <div role="group" aria-label="Show channels" className="flex flex-wrap gap-1.5">
        {(Object.keys(PLATFORMS) as Platform[]).map((platform) => (
          <button
            key={platform}
            type="button"
            aria-pressed={!hidden.has(platform)}
            onClick={() =>
              setHidden((current) => {
                const next = new Set(current);
                if (next.has(platform)) next.delete(platform);
                else next.add(platform);
                return next;
              })
            }
            className={cn(
              "rounded-md px-2 py-1 text-xs font-semibold",
              PLATFORM_STYLE[platform],
              hidden.has(platform) && "opacity-30",
            )}
          >
            {PLATFORMS[platform].short} {PLATFORMS[platform].name}
          </button>
        ))}
      </div>

      {calendar.isError && <ErrorText>{apiErrorMessage(calendar.error)}</ErrorText>}

      <div className="grid gap-5 xl:grid-cols-[minmax(0,1fr)_20rem]">
        <div className="overflow-x-auto">
          <div className="grid min-w-[44rem] grid-cols-7 gap-1.5">
            {["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"].map((day) => (
              <span key={day} className="px-1 text-xs font-semibold text-muted">
                {day}
              </span>
            ))}
            {days.map((day) => {
              const slots = byDay.get(day) ?? [];
              const inMonth = day.startsWith(month);
              return (
                <button
                  key={day}
                  type="button"
                  onClick={() => setSelected(day)}
                  aria-label={`${label(day, { weekday: "long", day: "numeric", month: "long" })}, ${slots.length} post${slots.length === 1 ? "" : "s"}`}
                  className={cn(
                    "flex min-h-28 flex-col gap-1 rounded-lg border p-1.5 text-left",
                    inMonth ? "bg-white" : "bg-paper/60 text-muted",
                    day === selected ? "border-accent ring-1 ring-accent" : "border-line",
                  )}
                >
                  <span className="flex items-center gap-1 text-xs font-semibold">
                    {Number(day.slice(8))}
                    {day === today && (
                      <span className="rounded-full bg-accent px-1.5 text-[10px] text-white">
                        Today
                      </span>
                    )}
                  </span>
                  {slots.slice(0, 4).map((slot) => (
                    <span
                      key={slot.id}
                      className={cn(
                        "truncate rounded px-1 py-0.5 text-[10px] leading-tight",
                        PLATFORM_STYLE[slot.channel],
                        slot.status === "proposed" &&
                          "bg-white text-ink outline-dashed outline-1 outline-[#8a93a6]",
                      )}
                    >
                      <strong>{PLATFORMS[slot.channel as Platform]?.short ?? slot.channel}</strong>{" "}
                      {slotStatus(slot)}
                    </span>
                  ))}
                  {slots.length > 4 && (
                    <span className="text-[10px] text-muted">+{slots.length - 4} more</span>
                  )}
                </button>
              );
            })}
          </div>
          {calendar.isPending && (
            <LoaderCircle className="mt-3 size-5 animate-spin text-muted" aria-label="Loading" />
          )}
        </div>
        <DayPanel day={selected} slots={byDay.get(selected) ?? []} />
      </div>
    </section>
  );
};

const PlanWeek = ({ today }: { today: string }) => {
  const request = useRequestPlan();
  const thisWeek = mondayOf(today);
  const [weekOf, setWeekOf] = useState(addDays(thisWeek, 7));
  return (
    <div className="flex flex-wrap items-end gap-2">
      <Field label="Plan a week">
        {(props) => (
          <select
            {...props}
            className={selectClass}
            value={weekOf}
            onChange={(event) => setWeekOf(event.target.value)}
          >
            {[0, 7, 14, 21].map((offset) => {
              const monday = addDays(thisWeek, offset);
              return (
                <option key={monday} value={monday}>
                  {offset === 0 ? "This week" : offset === 7 ? "Next week" : "Week"} of{" "}
                  {label(monday, { day: "numeric", month: "short" })}
                </option>
              );
            })}
          </select>
        )}
      </Field>
      <Button disabled={request.isPending} onClick={() => request.mutate({ weekOf })}>
        {request.isPending ? (
          <LoaderCircle className="size-4 animate-spin" />
        ) : (
          <Sparkles className="size-4" />
        )}
        Ask the director
      </Button>
      {request.isError && <ErrorText>{apiErrorMessage(request.error)}</ErrorText>}
    </div>
  );
};

const PlanBanner = ({ plan, count }: { plan: WeekPlanDto; count: number }) => {
  const { atLeast } = useActive();
  const approve = useApprovePlan();
  const request = useRequestPlan();
  const [note, setNote] = useState("");
  const [changing, setChanging] = useState(false);
  const week = label(plan.weekOf, { day: "numeric", month: "short" });

  if (plan.status === "planning") {
    return (
      <div className="flex items-center gap-2 rounded-xl border border-line bg-white p-4 text-sm text-muted">
        <LoaderCircle className="size-4 animate-spin" /> The director is planning the week of {week}
        …
      </div>
    );
  }
  if (plan.status === "failed") {
    return (
      <div className="flex flex-wrap items-center justify-between gap-2 rounded-xl border border-red-200 bg-red-50 p-4 text-sm">
        <span>
          Planning the week of {week} stopped: {plan.error}
        </span>
        {atLeast("EDITOR") && (
          <Button variant="secondary" onClick={() => request.mutate({ weekOf: plan.weekOf })}>
            Try again
          </Button>
        )}
      </div>
    );
  }
  return (
    <div className="space-y-3 rounded-xl border border-amber-200 bg-amber-50 p-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="max-w-3xl space-y-1 text-sm">
          <p>
            <strong>Week of {week}:</strong> the director proposed {count} post
            {count === 1 ? "" : "s"}. Nothing is made until the week is approved. Move or remove
            posts first if you like.
          </p>
          {plan.summary && <p className="text-muted">{plan.summary}</p>}
        </div>
        <div className="flex flex-wrap gap-2">
          {atLeast("REVIEWER") && (
            <Button
              disabled={approve.isPending || count === 0}
              onClick={() => approve.mutate(plan.id)}
            >
              {approve.isPending && <LoaderCircle className="size-4 animate-spin" />}
              Approve the week
            </Button>
          )}
          {atLeast("EDITOR") && (
            <Button variant="secondary" onClick={() => setChanging((open) => !open)}>
              Ask for changes
            </Button>
          )}
        </div>
      </div>
      {changing && (
        <div className="flex flex-wrap gap-2">
          <Input
            aria-label="What should change"
            className="min-w-72 flex-1"
            maxLength={1000}
            placeholder="e.g. More Instagram, nothing on Saturday"
            value={note}
            onChange={(event) => setNote(event.target.value)}
          />
          <Button
            disabled={note.trim().length < 3 || request.isPending}
            onClick={() =>
              request.mutate({ weekOf: plan.weekOf, note }, { onSuccess: () => setChanging(false) })
            }
          >
            Plan again
          </Button>
        </div>
      )}
      {(approve.error ?? request.error) && (
        <ErrorText>{apiErrorMessage(approve.error ?? request.error)}</ErrorText>
      )}
    </div>
  );
};

const DayPanel = ({ day, slots }: { day: string; slots: SlotDto[] }) => {
  const { atLeast } = useActive();
  const [adding, setAdding] = useState(false);
  return (
    <aside
      aria-label="Selected day"
      className="space-y-3 rounded-xl border border-line bg-white p-4"
    >
      <h2 className="font-serif text-xl text-ink">
        {label(day, { weekday: "long", day: "numeric", month: "long" })}
      </h2>
      {slots.length === 0 && <p className="text-sm text-muted">Nothing planned.</p>}
      {slots.map((slot) => (
        <SlotCard key={slot.id} slot={slot} />
      ))}
      {atLeast("EDITOR") &&
        (adding ? (
          <AddSlot day={day} onDone={() => setAdding(false)} />
        ) : (
          <Button variant="secondary" className="w-full" onClick={() => setAdding(true)}>
            <CalendarPlus className="size-4" /> Add something to this day
          </Button>
        ))}
    </aside>
  );
};

const SlotCard = ({ slot }: { slot: SlotDto }) => {
  const { atLeast } = useActive();
  const move = useMoveSlot();
  const cancel = useCancelSlot();
  const posted = slot.piece?.status === "posted";
  return (
    <div className="space-y-1.5 rounded-lg border border-line p-3">
      <span
        className={cn(
          "inline-block rounded px-1.5 py-0.5 text-[11px] font-semibold",
          PLATFORM_STYLE[slot.channel],
        )}
      >
        {PLATFORMS[slot.channel as Platform]?.name ?? slot.channel} · {formatLabel(slot.format)}
      </span>
      <p className="text-sm font-medium leading-snug text-ink">{slot.title}</p>
      {slot.brief && <p className="text-xs text-muted">{slot.brief}</p>}
      <p className="text-xs text-muted">
        {slot.time} · {slotStatus(slot)}
        {slot.source === "director" && slot.rationale && ` · ${slot.rationale}`}
      </p>
      {slot.piece && (
        <Link to={`/content/${slot.piece.id}`} className="text-xs text-accent underline">
          Open {slot.piece.code}
        </Link>
      )}
      {atLeast("EDITOR") && !posted && (
        <div className="flex flex-wrap items-center gap-1.5 pt-1">
          <Input
            type="date"
            aria-label="Move to"
            className="h-8 w-36 text-xs"
            value={slot.date}
            onChange={(event) =>
              event.target.value && move.mutate({ id: slot.id, date: event.target.value })
            }
          />
          <Input
            type="time"
            aria-label="Time"
            className="h-8 w-24 text-xs"
            value={slot.time}
            onChange={(event) =>
              event.target.value && move.mutate({ id: slot.id, time: event.target.value })
            }
          />
          <Button
            variant="ghost"
            aria-label="Take off the calendar"
            disabled={cancel.isPending}
            onClick={() => cancel.mutate(slot.id)}
          >
            <X className="size-4" />
          </Button>
        </div>
      )}
      {(move.error ?? cancel.error) && (
        <ErrorText>{apiErrorMessage(move.error ?? cancel.error)}</ErrorText>
      )}
    </div>
  );
};

const AddSlot = ({ day, onDone }: { day: string; onDone: () => void }) => {
  const settings = useContentSettings();
  const add = useAddSlot();
  const brandFormats = settings.data?.channels.flatMap((channel) => channel.formats) ?? [];
  const formats = (
    brandFormats.length ? brandFormats : Object.keys(CONTENT_FORMATS)
  ) as ContentFormat[];
  const [form, setForm] = useState({
    format: formats[0] ?? ("linkedin:text" as ContentFormat),
    time: "09:00",
    title: "",
    brief: "",
  });
  const submit = (event: FormEvent) => {
    event.preventDefault();
    add.mutate({ date: day, pillar: "", ...form }, { onSuccess: onDone });
  };
  return (
    <form onSubmit={submit} className="space-y-2 rounded-lg border border-line p-3">
      <Field label="Format">
        {(props) => (
          <select
            {...props}
            className={selectClass}
            value={form.format}
            onChange={(event) => setForm({ ...form, format: event.target.value as ContentFormat })}
          >
            {formats.map((format) => (
              <option key={format} value={format}>
                {CONTENT_FORMATS[format]}
              </option>
            ))}
          </select>
        )}
      </Field>
      <Field label="Time">
        {(props) => (
          <Input
            {...props}
            type="time"
            value={form.time}
            onChange={(event) => setForm({ ...form, time: event.target.value })}
          />
        )}
      </Field>
      <Field label="What it's about">
        {(props) => (
          <Input
            {...props}
            required
            minLength={3}
            maxLength={200}
            value={form.title}
            onChange={(event) => setForm({ ...form, title: event.target.value })}
          />
        )}
      </Field>
      <Field label="The angle (optional)">
        {(props) => (
          <Textarea
            {...props}
            rows={2}
            maxLength={1000}
            value={form.brief}
            onChange={(event) => setForm({ ...form, brief: event.target.value })}
          />
        )}
      </Field>
      {add.isError && <ErrorText>{apiErrorMessage(add.error)}</ErrorText>}
      <div className="flex gap-2">
        <Button type="submit" disabled={add.isPending}>
          {add.isPending ? (
            <LoaderCircle className="size-4 animate-spin" />
          ) : (
            <Plus className="size-4" />
          )}
          Add and draft it
        </Button>
        <Button type="button" variant="ghost" onClick={onDone}>
          Cancel
        </Button>
      </div>
    </form>
  );
};
